<script>
import { computed, reactive, toRefs, watch } from "vue";
import { useStore } from "vuex";
import auth from "@/compositions/modules/auth";
import SrUpload from "@/components/SrUpload";
import {
  addAgreementFile,
  getAgreementInfo,
} from "@/api/modules/auth";
import { vab, commonUtils } from "@libs/utils";
import { getRegisterInfo } from "@/utils/common-req";
import fileComp from "@/compositions/modules/file";
import {
  CodeToText,
  provinceAndCityData,
  TextToCode,
} from "element-china-area-data";
import { validatorComp } from "@/domain/base";
import { useI18n } from "@/plugins/i18n";

const { throttle } = commonUtils;

export default {
  name: "StepControl",
  components: { SrUpload },
  setup() {
    const { t, locale } = useI18n();
    const store = useStore();
    const { FILE } = store.state.base.constant;
    const _initSteps = initSteps();
    const _initProtocol = initProtocol();
    const _initAuth = initAuth();
    const _initTitle = initTitle();
    const { template } = fileComp();

    const scopedComputed = {
      countryTemp: computed(() => store.state.base.countryTemp),
      curTemp: computed(() => {
        let options = [];
        let mappings = {};
        for (let item in store.state.base.curTemp) {
          mappings[item] = item;
          if (!(item === "CNY" || item === "CNH")) {
            options.push({ label: item, value: item });
          }
        }
        return { options, mappings };
      }),
      // 拆分四个步骤的计算属性，便于平铺使用
      step1: computed(() => ({
        key: "submitRegister",
        status: "CF", // 提交注册步骤默认通过
      })),
      step2: computed(() => ({
        key: "kycReviewStatus",
        status: store.state.auth?.registerInfo?.kycReviewStatus || "",
      })),
      step3: computed(() => ({
        key: "agreementReviewStatus",
        status: store.state.auth?.registerInfo?.agreementReviewStatus || "",
      })),
      // 语言判断计算属性
      // localCN: computed(() => locale.value === "zh-CN"),
      // 当前激活步骤逻辑 - 同一时间只能有一个步骤被激活
      currentStep: computed(() => {
        let step = 1;
        const steps = [
          { key: "submitRegister", status: "CF" }, // 步骤1：提交注册，默认通过
          { key: "kycReviewStatus", status: store.state.auth?.registerInfo?.kycReviewStatus || "" },
          { key: "agreementReviewStatus", status: store.state.auth?.registerInfo?.agreementReviewStatus || "" }
        ];

        if (steps[1].status === 'CF') {
          step++
        }

        if (steps[2].status === 'CF') {
          step++
        }
        console.log(steps)

        return step;
      })
    };

    const state = reactive({
      serviceProtocol: template.serviceProtocol,
      bizIntAgreement: template.bizIntAgreement,
      registerInfo: store.state.auth.registerInfo,
      provinceAndCityData,
      bankCtryCodeEnum: store.state.base.dict.country.map,
    });

    const scopedMethods = {
      showToastBeforeUpload(status) {
        if (status !== "CF") {
          vab.baseMessage(t("dashboard.step.other.text_1"), "error");
        }
      },
    };



    // 上传监听
    watch(
      () => _initProtocol.protocolForm.agreementFileId,
      async (value) => {
        if (value) {
          try {
            const params = {
              agreementFileId: _initProtocol.protocolForm.agreementFileId,
            };
            await addAgreementFile(params);
            vab.baseMessage(t("global.content.text_3"), "success");
            await getRegisterInfo();
          } catch (err) {
            vab.baseMessage(t("global.content.text_4"), "error");
          }
        }
      }
    );

    return {
      ...toRefs(state),
      ...scopedMethods,
      ...toRefs(_initSteps),
      ...toRefs(_initProtocol),
      ...toRefs(_initAuth),
      ...toRefs(_initTitle),
      ...scopedComputed,
      FILE,
      t,
    };
  },
};

// 步骤条相关逻辑
const initSteps = () => {
  const store = useStore();
  const dashboardStep = computed(() => [
    {
      key: "submitRegister",
      status: "CF", // 提交注册步骤默认通过
    },
    {
      key: "kycReviewStatus",
      status: store.state.auth?.registerInfo?.kycReviewStatus || "",
    },
    {
      key: "agreementReviewStatus",
      status: store.state.auth?.registerInfo?.agreementReviewStatus || "",
      
    },
  ]);
  return { dashboardStep };
};

// 初始化协议
const initProtocol = () => {
  const protocolForm = reactive({
    agreementFileId: "",
    agreementFileName: "",
    agreementRejectRemark: null,
    agreementCheckStatus: "",
  });
  const state = reactive({});

  const { getFileRemoteInfo } = fileComp();
  const scopedMethods = {
    // 下载
    downFile: (url, fileName) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    },
    fetchAgreementInfo: async () => {
      const res = await getAgreementInfo();
      protocolForm.agreementRejectRemark = res.agreementRejectRemark;
      protocolForm.agreementCheckStatus = res.agreementCheckStatus;
      return res;
    },
    // 查看已上传协议
    handleViewPact: () => {
      const fetchProtocol = async () => {
        const res = await scopedMethods.fetchAgreementInfo();
        const { agreementFileId } = res;
        const { fileName, url } = await getFileRemoteInfo(agreementFileId);
        scopedMethods.downFile(url, fileName);
      };
      fetchProtocol();
    },
  };
  scopedMethods.fetchAgreementInfo();
  return { ...toRefs(state), protocolForm, ...scopedMethods };
};

// 处理title
const initTitle = () => {
  const { t } = useI18n();
  const scopedMethods = {
   
  };

  return { ...scopedMethods };
};

// 初始化认证
const initAuth = () => {
  const scopedMethods = {
    handleAuth: () => {
      auth.toAuth();
    },
    // 查看驳回原因
    handleErrPage: () => {
      auth.toAuth();
    },
  };
  return { ...scopedMethods };
};


</script>

<template>
  <div class="step-container" v-if="currentStep < 3">
    <el-row style="width: 100%;">
      <!-- 步骤1：提交注册 -->
      <el-col :span="8" :class="['step-card', 'step-card-1', { 'step-card-active': currentStep === 0 }]">
        <div class="step-card-symbol">1</div>
        <div class="step-card-title">
          {{ $t('dashboard.step.title.accountRegistration') }}
        </div>
        <div class="step-card-desc">
          <div>{{ t('dashboard.step.content.accountRegistrationTip') }}</div>
        </div>
        <div class="step-card-status step-card-status-active">
          <el-icon style="margin-right:5px">
            <CircleCheck />
          </el-icon>
          {{ t('dashboard.step.content.accountRegistrationTip2') }}
        </div>
      </el-col>
      <!-- 步骤2：KYC审核 -->
      <el-col :span="8" :class="['step-card', 'step-card-2', { 'step-card-active': currentStep === 1 }]">
        <div class="step-card-symbol">2</div>
        <div class="step-card-title">
          {{ $t('dashboard.step.title.identityVerification') }}
        </div>
        <div class="step-card-desc">
          <div v-if="(['CF'].includes(step2.status))">
            {{ $t('dashboard.step.content.identityVerificationTipCF') }}
          </div>
          <div v-else-if="['CP', 'RC'].includes(step2.status)">{{ t('dashboard.step.content.identityVerificationTipCP')
            }}
          </div>
          <div v-else-if="['ST'].includes(step2.status)">{{ t('dashboard.step.content.text_3') }}
          </div>
          <div v-else-if="['RJ'].includes(step2.status)">{{ t('dashboard.step.content.identityVerificationTipRJ') }}
          </div>
          <div v-else>
            {{ t('dashboard.step.content.identityVerificationTip', {
              regType: +registerInfo.regType === 1 ?
                t('dashboard.step.content.text_1_3') : t('dashboard.step.content.text_1_2')
            }) }}
          </div>
        </div>
        <div class="step-card-status step-card-status-active" style="left: 40px;"
          v-if="(['CF'].includes(step2.status))">
          <el-icon style="margin-right:5px">
            <CircleCheck />
          </el-icon>
          {{ t('dashboard.step.content.identityVerificationTipCF2') }}
        </div>
        <div class="step-card-btn">
          <el-button v-if="(!['CF'].includes(step2.status))">
            <div v-if="['CP', 'RC'].includes(step2.status)">
              <div @click="handleAuth">{{ t('dashboard.step.content.text_15') }}</div>
            </div>
            <div v-else-if="['ST'].includes(step2.status)">
              <div @click="handleErrPage">{{ t('dashboard.step.content.text_15') }}</div>
            </div>
            <div v-else-if="['RJ'].includes(step2.status)">
              <div @click="handleErrPage">{{ t('dashboard.step.content.text_4_1') }}</div>
            </div>
            <div v-else>
              <div @click="handleAuth">{{ t('dashboard.step.content.identityVerificationTip2') }}</div>
            </div>
          </el-button>
        </div>
      </el-col>

      <!-- 步骤3：协议审核 -->
      <el-col :span="8" :class="['step-card', 'step-card-3', { 'step-card-active': currentStep === 2 }]">
        <div class="step-card-symbol">3</div>
        <div class="step-card-title">
          {{ $t('dashboard.step.title.signAgreement') }}
        </div>
        <div class="step-card-desc">
          <div v-if="(['CF'].includes(step3.status))">{{ t('dashboard.step.content.text_7') }}1</div>
          <div v-else-if="['CP', 'RC'].includes(step3.status)">{{ t('dashboard.step.content.text_8') }}
            {{ t('dashboard.step.content.signAgreementTipCP') }}
          </div>
          <div v-else-if="['RJ', 'ST'].includes(step3.status)">
            {{ t('dashboard.step.content.signAgreementTipRJ') }}
            <span v-if="localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                  :href="serviceProtocol" download="《寻汇营销会员合作协议》">{{ t('dashboard.step.content.signAgreementTip2')
                  }}</a></span>
              <span v-else-if="!localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                  :href="bizIntAgreement" download="Business Introduction Agreement">&nbsp;{{
                    t('dashboard.step.content.signAgreementTip2') }}&nbsp;</a></span>

            <div class="red overflow-text" v-if="['RJ', 'ST'].includes(protocolForm.agreementCheckStatus)">{{
              t('dashboard.step.content.text_17') }}:{{ protocolForm.agreementRejectRemark }}</div>
          </div>
          <div v-else>
            <span v-if="localCN">请联系您的客户经理获取对应协议模板</span>
            <span v-else-if="!localCN">Please contact your account manager for the agreement template.</span>
            <!-- <span v-if="localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                  :href="serviceProtocol" download="《寻汇营销会员合作协议》">{{ t('dashboard.step.content.signAgreementTip2')
                  }}</a></span>
              <span v-else-if="!localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                  :href="bizIntAgreement" download="Business Introduction Agreement">&nbsp;{{
                    t('dashboard.step.content.signAgreementTip2') }}&nbsp;</a></span> -->
          </div>
        </div>
        <div class="step-card-status step-card-status-active" style="left: 40px;"
          v-if="(['CP'].includes(step3.status))">
          <el-icon style="margin-right:5px">
            <CircleCheck />
          </el-icon>
          {{ t('dashboard.step.content.signAgreementTipCP2') }}
        </div>
        
        <div class="step-card-btn" v-else>
          <el-button v-if="['RC'].includes(step3.status)">

          </el-button>
          <div v-else class="step-card-btn-wrap">
            <!-- <el-button>
              <span v-if="localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                  :href="serviceProtocol" download="《寻汇营销会员合作协议》">{{ t('dashboard.step.content.signAgreementTip2')
                  }}</a></span>
              <span v-else-if="!localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                  :href="bizIntAgreement" download="Business Introduction Agreement">&nbsp;{{
                    t('dashboard.step.content.signAgreementTip2') }}&nbsp;</a></span>
            </el-button> -->
            <el-button>
              <sr-upload v-model:fileId="protocolForm.agreementFileId" v-model:fileName="protocolForm.agreementFileName"
                :filePrefix="FILE.PREFIX.AGREEMENT" fileSize="15"
                :mode="registerInfo.kycReviewStatus === 'CF' ? FILE.UPLOAD_MODE.EDITABLE : FILE.UPLOAD_MODE.READONLY">
                <div :class="['a']" @click="showToastBeforeUpload(registerInfo.kycReviewStatus)">
                  {{ t('dashboard.step.content.signAgreementTip3') }}</div>
              </sr-upload>
            </el-button>
          </div>



          <!-- <el-button v-if="(['ST', 'RJ'].includes(step3.status))">
            <span v-if="localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                :href="serviceProtocol" download="《寻汇营销会员合作协议》">{{ t('dashboard.step.content.signAgreementTip2')
                }}</a></span>
            <span v-else-if="!localCN"><a :class="['a', registerInfo.kycReviewStatus === 'CF' ? '' : 'gray']"
                :href="bizIntAgreement" download="Business Introduction Agreement">&nbsp;{{
                  t('dashboard.step.content.signAgreementTip2') }}&nbsp;</a></span>
          </el-button>
          <el-button v-if="(!['CF'].includes(step3.status))">
            <div v-if="['RC'].includes(step3.status)">
              <div @click="handleAuth">{{ t('dashboard.step.content.text_15') }}</div>
            </div>
            <div v-else-if="['ST', 'RJ'].includes(step3.status)">
              <sr-upload v-model:fileId="protocolForm.agreementFileId" v-model:fileName="protocolForm.agreementFileName"
                :filePrefix="FILE.PREFIX.AGREEMENT" fileSize="15"
                :mode="registerInfo.kycReviewStatus === 'CF' ? FILE.UPLOAD_MODE.EDITABLE : FILE.UPLOAD_MODE.READONLY">
                <div :class="['a']" @click="showToastBeforeUpload(registerInfo.kycReviewStatus)">
                  {{ t('dashboard.step.content.text_10_3') }}</div>
              </sr-upload>
            </div>
            <div v-else>
              <sr-upload v-model:fileId="protocolForm.agreementFileId" v-model:fileName="protocolForm.agreementFileName"
                :filePrefix="FILE.PREFIX.AGREEMENT" fileSize="15"
                :mode="registerInfo.kycReviewStatus === 'CF' ? FILE.UPLOAD_MODE.EDITABLE : FILE.UPLOAD_MODE.READONLY">
                <div :class="['a']" @click="showToastBeforeUpload(registerInfo.kycReviewStatus)">
                  {{ t('dashboard.step.content.text_10_3') }}</div>
              </sr-upload>
            </div>
          </el-button> -->
        </div>
        <div class="step-card-status-faild" v-if="['RJ', 'ST'].includes(step3.status)">
          <el-icon style="margin-right:5px">
            <Warning />
          </el-icon>
          {{ t('dashboard.step.content.signAgreementTipRJ4') }}
        </div>
      </el-col>
    </el-row>


  </div>
</template>

<style lang="scss" scoped rel="stylesheet/scss">
.step-container {
  padding: 14px 12px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  background-color: #fff;
  border-radius: 4px;
  position: relative;

  .step-wrap {
    flex: 1;
    margin-right: 14px;

    &:last-child {
      margin-right: 0;
    }
  }

  .step-card {
    height: 178px;
    border-radius: 10px 10px 10px 10px;
    overflow: hidden;
    position: relative;
    padding: 20px;
    background: #F4F9FF;

    .step-card-symbol {
      position: absolute;
      right: 38px;
      top: 5px;
      justify-content: center;
      color: #A5A5A5;
      opacity: 0.1;
      font-weight: bold;
      font-size: 200px;
    }

    .step-card-title {
      font-weight: bold;
      font-size: 16px;
      color: #333333;
      white-space: nowrap;
    }

    .step-card-status {
      position: absolute;
      top: 125px;
      left: 20px;
      display: flex;
      align-items: center;
    }

    .step-card-status-active {
      color: #209B75;
    }
    .step-card-status-faild{
      position: absolute;
      top: 0;
      right: 0px;
      display: flex;
      align-items: center;
      background-color: #FFE5E0;
      color: #F44336;
          padding: 7px 7px;
    border-radius: 0 0 0 10px;
    }


    .step-card-btn {
      position: absolute;
      bottom: 20px;
      left: 40px;
      display: flex;
      align-items: center;

      button {
        background: #FFF;
        color: #409EFF;
      }

      div button {
        background: #FFF;
        color: #409EFF;
      }
    }

    .step-card-btn-wrap {
      display: flex;
      button {
        background: #FFF;
        color: #409EFF;
      }
    }

    .step-card-desc {
      font-size: 14px;
      color: #666666;
      line-height: 20px;
      text-align: left;
      margin-top: 8px;
      position: relative;

      .a {
        color: #FFF;
        cursor: pointer;
        display: inline;
        text-decoration: underline;

      }

      .red {
        color: #FFF;
      }

      .gray {
        color: #666;
      }
    }
  }

  .step-card-1 {
    clip-path: polygon(0% 0%, 96% 0, 100% 50%, 96% 100%, 0% 100%);
  }

  .step-card-2 {
    clip-path: polygon(96% 0, 100% 50%, 96% 100%, 0% 100%, 4% 50%, 0% 0%);
    padding-left: 40px;
  }

  .step-card-3 {
    clip-path: polygon(100% 0, 100% 50%, 100% 99%, 0% 100%, 4% 50%, 0% 0%);
    padding-left: 40px;
  }

  .step-card-active {
    &.step-card {
      // background: linear-gradient(82deg, #d8ebff 0%, #fdfdfd 100%);
      background: #409EFF;

      .step-card-title {
        color: #FFF;
      }

      .step-card-desc {
        color: #FFF;
      }

      .step-card-status-active {
        color: #FFF;
      }

      & .step-card-symbol {
        font-weight: bold;
        color: #F4F9FF;
      }
    }

    .gray {
      color: #FFF;
    }
  }

  .hide {
    display: none;
  }

  .add-card-container {
    .bank-auth-status {
      height: 31px;
      border-radius: 3px;
      font-size: 14px;
      letter-spacing: -1px;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 5px 11px;
    }

    .bank-auth-status-CF {
      color: #00acea;
      background-color: #eef9fd;
    }

    .bank-auth-status-RJ,
    .bank-auth-status-ST {
      color: #e85f5f;
      background-color: #fdf4f4;
    }

    .bank-auth-status-CP,
    .bank-auth-status-WC,
    .bank-auth-status-RC {
      color: #fea801;
      background-color: #fff9ee;
    }
  }

  .upload-box {
    /*position: absolute;*/
    /*top: -5000px;*/
  }

  .auth-step-wrap {
    flex: 1;
    margin-right: 50px;
    overflow: hidden;
    display: flex;
    /*min-width: 240px;*/

    .auth-step__icon {
      width: 24px;
      height: 24px;
      color: #b1b1b1;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;

      .auth-step__success {
        display: inline-block;
        width: 24px;
        height: 24px;
        background-image: url("../../../assets/img/auth/success.png");
        background-size: 24px 24px;
      }

      .auth-step__error {
        display: inline-block;
        width: 24px;
        height: 24px;
        background: url("../../../assets/img/auth/error.png") 0 0 no-repeat;
        background-size: 24px 24px;
      }

      .auth-step__default {
        width: 24px;
        height: 24px;
        border-radius: 24px;
        border: 1px solid #b1b1b1;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .auth-step__current {
        width: 24px;
        height: 24px;
        border-radius: 24px;
        color: #fff;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #9ff4ce;
      }
    }

    .auth-step__title {
      flex: 1;
      font-size: 16px;
      margin-left: 10px;
      color: #b1b1b1;

      .red {
        color: #e85f5f;
      }

      .gray {
        color: #b1b1b1;
      }

      .black {
        color: #002d4f;
      }

      &>div:nth-of-type(2) {
        margin-top: 20px;
        font-size: 14px;
      }
    }
  }
}

:deep(.el-form-item__label) {
  padding: 0 30px 0 0 !important;
}

:deep(.el-form-item__content) {
  flex: inherit !important;
  width: auto !important;

  .el-input__inner,
  .el-textarea__inner {
    width: 288px !important;
    border-radius: 4px !important;
  }

  .el-select,
  .el-cascader {

    .el-input__inner,
    .el-textarea__inner {
      width: 268px !important;
      border-radius: 4px !important;
    }
  }

  .el-input {
    width: auto;
  }
}
</style>

<style lang="scss" rel="stylesheet/scss">
.reset-dialog-container {
  .dialog-body {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }

  .dialog-topic {
    background-color: #fffcf5;
    padding: 14px 31px;
    border-radius: 4px;
    margin-bottom: 20px;

    .dialog-topic-item {
      font-size: 13px;
      font-family: PingFangSC-Regular, PingFang SC, serif;
      font-weight: 400;
      color: #eb753c;
      line-height: 22px;
    }
  }

  .el-dialog__header {
    margin: 0;
    padding: 0;
    border-radius: 3px;
    overflow: hidden;
  }

  .dialog-header {
    height: 70px;
    padding-left: 35px;
    background-color: #f5fcfe;
    display: flex;
    align-items: center;
  }

  .dialog-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
}
.overflow-text{
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>